import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '~/libs/api.ts';
import { thunkErrorHandler } from '../../utils.ts';
import {
  TDiiaGetStatusAssetManagerRequest,
  TDiiaGetStatusRequest,
  TDiiaGetStatusResponse,
  TDiiaInitializeAssetManagerRequest,
  TDiiaInitializeResponse,
} from '../types.ts';
import { TBaseRequest } from '~/store/types.ts';

export const diiaVerificationInitialize = createAsyncThunk<TDiiaInitializeResponse, TBaseRequest>(
  'diia/identification/diiaVerificationInitialize',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/identification/diia/sharing/application/${applicationId}/initialize`
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const diiaVerificationInitializeAssetManager = createAsyncThunk<
  TDiiaInitializeResponse,
  TDiiaInitializeAssetManagerRequest
>(
  '/identification/diia/identification/diiaVerificationAssetManagerInitialize',
  async ({ applicationId, managerId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `identification/diia/sharing/application/${applicationId}/asset-managers/${managerId}/initialize`
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const diiaVerificationGetStatusAssetManager = createAsyncThunk<
  TDiiaGetStatusResponse,
  TDiiaGetStatusAssetManagerRequest
>(
  'diia/identification/diiaVerificationAssetManagerGetStatus',
  async ({ requestId, managerId, applicationId }, { rejectWithValue, signal }) => {
    try {
      const { data } = await api.get(
        `/identification/diia/sharing/application/${applicationId}/asset-managers/${managerId}/get-status/${requestId}`,
        {
          signal,
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const diiaVerificationGetStatus = createAsyncThunk<
  TDiiaGetStatusResponse,
  TDiiaGetStatusRequest
>(
  'diia/identification/diiaVerificationGetStatus',
  async ({ requestId, applicationId }, { rejectWithValue, signal }) => {
    try {
      const { data } = await api.get(
        `/identification/diia/sharing/application/${applicationId}/get-status/${requestId}`,
        {
          signal,
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const diiaVerificationGetFinalStatus = createAsyncThunk<
  TDiiaGetStatusResponse,
  TDiiaGetStatusRequest
>(
  'diia/identification/diiaVerificationGetFinalStatus',
  async ({ requestId, applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/identification/diia/sharing/application/${applicationId}/get-final-status/${requestId}`
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const diiaVerificationGetFinalStatusAssetManager = createAsyncThunk<
  TDiiaGetStatusResponse,
  TDiiaGetStatusAssetManagerRequest
>(
  'diia/identification/diiaVerificationGetFinalStatusAssetManager',
  async ({ applicationId, requestId, managerId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/identification/diia/sharing/application/${applicationId}/asset-managers/${managerId}/get-final-status/${requestId}`
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const diiaVerificationRetrieveDocuments = createAsyncThunk<
  TDiiaGetStatusResponse,
  TDiiaGetStatusRequest
>(
  'diia/identification/diiaVerificationRetrieveDocuments',
  async ({ applicationId, requestId }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/identification/diia/sharing/application/${applicationId}/retrieve-documents/${requestId}`,
        null
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const diiaVerificationRetrieveDocumentsAssetManager = createAsyncThunk<
  TDiiaGetStatusResponse,
  TDiiaGetStatusAssetManagerRequest
>(
  'diia/identification/diiaVerificationRetrieveDocumentsAssetManager',
  async ({ applicationId, requestId, managerId }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/identification/diia/sharing/application/${applicationId}/asset-managers/${managerId}/retrieve-documents/${requestId}`,
        null
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const updateFromDiiaVerification = createAsyncThunk<{ status: string }, TBaseRequest>(
  'diia/identification/updateFromDiiaVerification',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/identification/application/${applicationId}/update-from-diia`,
        null
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const deleteDiiaVerification = createAsyncThunk<{ status: string }, TBaseRequest>(
  'diia/identification/deleteDiiaVerification',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(
        `/identification/application/${applicationId}/documents/video-verification`
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);
